<template>
  <div class="d-flex justify-end text-no-wrap">
    <app-button
      v-if="withQuestionsLink"
      type="icon"
      @click="navigateToDocumentQuestions"
    >
      <app-material-icon
        name="messages"
        type="fa"
        size="sm"
        weight="far"
        color="#4F565D"
      />
      <span
        v-if="document.questionsIds.length > 1"
        class="ml-1 font-weight-bold"
      >
        {{ document.questionsIds.length }}
      </span>
    </app-button>

    <OfficeOnlineEditionButton v-if="isOfficeEditionButtonShowed" :document="document"/>

    <AppTooltip v-if="document.canDownload">
      <template #activator="{props}">
        <AppDownloadFile v-if="document.type === 'file'" :link="fileLink">
          <template #default="{ submitForm }">
            <app-button type="icon"
                        data-locator="download-button"
                        v-bind="props"
                        @click="downloadFile(submitForm)"
            >
              <app-icon icon-name="download" icon-weight="fas" size="small"/>
            </app-button>
          </template>
        </AppDownloadFile>
      </template>
      <span>{{ $t('common.download') }}</span>
    </AppTooltip>

    <DocumentsTableItemMenu v-if="!document.specialFolder"
                            :document="document"
                            @openInFolder="openInFolder"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import OfficeOnlineEditionButton from '@/common/office/OfficeOnlineEditionButton.vue'
import { getPdfContentType, USER_ROLE, track } from '@/common/pendo/agent'
import { roomFileDownloadUrl } from '@/common/utils/files'
import { Filter } from '@/models/filter.model'
import DocumentsTableItemMenu from '@/project/documents/table/DocumentsTableItemMenu.vue'
import router, {
  QUESTION_ANSWER_ITEM_ROUTE_NAME,
  QUESTION_ANSWER_LIST_ROUTE_NAME,
} from '@/router'
import { QuestionAnswerMutationTypes } from '@/store/modules/question-answer/mutation_types'

export default {
  name: 'DocumentsTableItemOptions',
  components: {
    AppDownloadFile,
    AppTooltip,
    DocumentsTableItemMenu,
    OfficeOnlineEditionButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomMnemo', 'isWopiEnabled', 'isCurrentUserPm']),
    fileLink () {
      return roomFileDownloadUrl(this.document.id, this.roomMnemo)
    },
    withQuestionsLink () {
      return this.document.type === 'file' && this.document.questionsIds?.length > 0
    },
    isOfficeEditionButtonShowed () {
      return this.isWopiEnabled && this.document.canEdit && this.isCurrentUserPm
    },
  },
  methods: {
    ...mapMutations('questionAnswer', [QuestionAnswerMutationTypes.SET_FILTERS]),
    navigateToDocumentQuestions () {
      if (this.document.questionsIds?.length === 1) {
        router.push({
          name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
          params: { questionId: this.document.questionsIds[0] },
        })
      } else {
        const referenceFilter = new Filter('references', this.$t('project.documents.table.DocumentsTableItemOptions.referenceFilter'), this.document.id, this.document.basename)
        this.SET_FILTERS([referenceFilter])

        router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
      }
    },
    openInFolder () {
      this.$emit('openInFolder')
    },
    downloadFile (callback) {
      track('document_downloaded', {
        document_id: this.document.id,
        project_mnemo: this.roomMnemo,
        user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
        document_type: this.document.ext,
        entrypoint: 'document_list',
        pdf_content_type: getPdfContentType(this.document),
      })
      callback()
    },
  },
}
</script>

<style scoped lang="scss">
.DocumentTableItemOptions-iconContainer {
  width: 36px
}
</style>
